import { useCallback, useEffect, useRef, useState } from 'react';
import {
  AudioBus,
  StoryLayer,
  STORY_SOUNDS,
  loadStoryArt,
  potEmpty,
  sideLights,
  type BeatHandlers,
  type BoardRunView,
  type GiftPrize,
  type MiniGameEnd,
  type PlayerProfile,
  type SlotDemoScene,
  type StoryArtSources,
} from '@goodtime/engine';
import type { Beat, PlayOffer, RoadState, SpinSlotResponse } from '@goodtime/shared';
import { funnelBeacon } from '../api/funnel';
import { declinePlay } from '../api/plays';
import { withRetry } from '../api/road';
import { acceptGift, acceptVisitor, fetchTakings } from '../api/story';
import { claimDailyBonus } from '../api/wallet';
import { storyBridge } from '../story/storyBridge';

/** When this app session began: the takings card counts from here. */
const SESSION_STARTED = Date.now();
/** A session this short has no takings card. */
const TAKINGS_MIN_SPINS = 5;

export interface StoryOptions {
  /** `road_story` is on for the player, and the road is on this slot. */
  enabled: boolean;
  sessionId: string | null;
  playerId: string | null;
  profile: PlayerProfile;
  /** The slot scene, read when needed (it changes as slots open). */
  getScene: () => SlotDemoScene | null;
  /** The slot on screen. */
  themeId: string | null;
  /** Another slot's name, for the morning card (it names the slot whose
   *  side lights it lit). */
  slotNameOf: (themeId: string) => string | undefined;
  /** The road on this slot (null until loaded). */
  road: RoadState | null;
  /** The run as the player sees it. */
  run: BoardRunView;
  /** Words for this slot. */
  words: { collectorNoun: string; slotName: string; runName: string };
  /** The story's art for this slot (its collector, wild and ring). */
  art: StoryArtSources;
  /** Pull the server wallet into the profile. */
  syncWallet: () => Promise<void>;
  /** Play an offered game on the slot to its end (the road's own game
   *  player: its coins fly in, its free spins join the slot's). */
  openGame: (offer: PlayOffer) => Promise<MiniGameEnd>;
}

/**
 * Royal Road's story on the open slot (Phase 3): mounts the story layer on
 * the slot canvas, holds each spin's beat until the reels are idle and then
 * plays it, opens gifts on the server (their coins fly to the wallet), lets
 * Fen react, claims the day's bonus as the morning card on the first visit
 * of the day, and shows the takings card on the session's first exit.
 */
export function useStory(o: StoryOptions) {
  const layerRef = useRef<StoryLayer | null>(null);
  const pendingBeat = useRef<Beat | null>(null);
  const playing = useRef(false);
  const paidSpins = useRef(0);
  const quietSpins = useRef(0);
  const saidFirstCollect = useRef(false);
  const saidQuiet = useRef(false);
  const takingsShown = useRef(false);
  /** The slot the welcome, the morning card and a waiting gift were last
   *  checked on. */
  const checkedSlot = useRef<string | null>(null);
  /** A first visit meets the reels before Fen (producer 2026-11-03): his welcome waits for the first spin here to
   *  land and the reels to settle. This lets it go, from onSlotIdle. */
  const welcomeGo = useRef<(() => void) | null>(null);
  const spunHere = useRef(false);
  const lastRun = useRef<BoardRunView | null>(null);
  const [mounted, setMounted] = useState(0);
  const opts = useRef(o);
  opts.current = o;

  // Mount the layer on the slot canvas while the story is on for this slot.
  useEffect(() => {
    if (!o.enabled) return;
    const scene = o.getScene();
    if (!scene) return;
    let layer: StoryLayer | null = null;
    let cancelled = false;
    // The host has the scene before it's built: wait for it, so the layer
    // goes over the slot and the boards get the reel floor.
    void Promise.all([loadStoryArt(o.art), scene.whenBuilt()]).then(([art]) => {
      if (cancelled) return;
      void AudioBus.shared.loadEntries(storyAudioEntries());
      layer = new StoryLayer({
        art,
        floor: scene.floorTexture(),
        flyCoinsToWallet: (from, amount) => scene.flyCoinsToWallet(from, amount),
        playTitleCard: (text, options) => scene.playTitleCard(text, options),
      });
      scene.mountStory(layer);
      layerRef.current = layer;
      setMounted((n) => n + 1);
    });
    return () => {
      cancelled = true;
      if (layer) layer.destroy({ children: true });
      layerRef.current = null;
    };
    // The scene instance changes when a slot opens; re-read it then.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [o.enabled, o.sessionId, o.getScene()]);

  // Keep the words and settings current.
  useEffect(() => {
    layerRef.current?.configure({
      words: o.words,
      storyOn: o.profile.storyMode !== 'off',
      chatter: o.profile.fenChatter,
    });
    // The words are compared by value.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mounted, o.words.collectorNoun, o.words.slotName, o.words.runName, o.profile]);
  useEffect(() => {
    let mode = o.profile.storyMode;
    const sub = o.profile.events.on('story-pref-changed', (e) => {
      layerRef.current?.configure({ words: opts.current.words, storyOn: e.storyMode !== 'off', chatter: e.fenChatter });
      // Fen back from Classic mode says hello.
      if (mode === 'off' && e.storyMode === 'on') layerRef.current?.greetBack();
      mode = e.storyMode;
      funnelBeacon('story_mode', { playerId: opts.current.playerId, detail: e.storyMode });
    });
    return () => sub.dispose();
  }, [o.profile]);

  /** Open a gift on the server; set the balance to before it so its coins
   *  can fly in, and bank its free spins and gems. */
  const open = useCallback(async (playId: string): Promise<GiftPrize> => {
    const { sessionId, getScene, profile } = opts.current;
    if (!sessionId) throw new Error('no session');
    const res = await withRetry(() => acceptGift(playId, sessionId));
    const scene = getScene();
    scene?.syncBalance(res.wallet.coins - res.prize.coins);
    if (res.prize.freeSpins > 0) scene?.addFreeSpins(res.prize.freeSpins);
    if (res.prize.gems > 0) profile.awardGems(res.prize.gems);
    return res.prize;
  }, []);

  /** Take up a visitor's game, or the treasure map's dig, and play it to its
   *  end; null when it can't be opened (the server lets a visitor it never
   *  heard back on lapse, and digs a treasure up at the next spin). */
  const play = useCallback(async (playId: string): Promise<GiftPrize | null> => {
    const { sessionId, openGame } = opts.current;
    if (!sessionId) return null;
    try {
      const visit = await withRetry(() => acceptVisitor(playId, sessionId));
      const end = await openGame(visit.offer);
      return end.prize;
    } catch {
      return null;
    }
  }, []);

  /** Send a visitor on their way. */
  const sendOn = useCallback(async (playId: string): Promise<void> => {
    const { sessionId } = opts.current;
    if (sessionId) await withRetry(() => declinePlay(playId, sessionId)).catch(() => undefined);
  }, []);

  const playBeat = useCallback(
    async (beat: Beat) => {
      const layer = layerRef.current;
      if (!layer) return;
      playing.current = true;
      funnelBeacon('beat_seen', { playerId: opts.current.playerId, detail: beat.kind });
      const handlers: BeatHandlers = { open, play, sendOn };
      try {
        await layer.playBeat(beat, handlers);
        if (beat.kind === 'mod') layer.setMods(opts.current.road?.story?.mods);
      } finally {
        playing.current = false;
        void opts.current.syncWallet();
      }
    },
    [open, play, sendOn],
  );

  /** The first visit of the day to a story slot: claim the day's bonus and
   *  show it as the morning card (the server lights the side lights). */
  const morning = useCallback(async () => {
    const { playerId, profile, getScene } = opts.current;
    const layer = layerRef.current;
    if (!layer || !playerId || !profile.canClaimDailyBonus()) return;
    let claim;
    try {
      claim = await claimDailyBonus(playerId);
    } catch {
      return;
    }
    if (!claim.claimed) return;
    playing.current = true;
    funnelBeacon('morning_card', { playerId, detail: claim.readyLights ? 'lights' : 'coins' });
    getScene()?.syncBalance(claim.wallet.coins - claim.reward);
    // The server lights the road played last, which may be another slot:
    // the card names that one.
    const lit = claim.readyLights;
    await layer.showMorning(claim, lit ? opts.current.slotNameOf(lit.themeId) : undefined);
    // The lights lit overnight come on now, if they're this slot's.
    const road = opts.current.road;
    if (road && lit && lit.themeId === opts.current.themeId) {
      const lights = sideLights({ ...road, ready: lit.ready, readyProgress: 0 });
      getScene()?.setSideLights({ lit: lights.lit, next: lights.next });
    }
    profile.applyDailyBonusClaim({ ...claim, coins: claim.wallet.coins });
    playing.current = false;
    await opts.current.syncWallet();
  }, []);

  // Once the layer and the road are there: Fen's welcome (first time, once
  // the first spin has settled) or his hello on a road that's new to the
  // player, the morning card, and a gift left unopened (after a reload).
  useEffect(() => {
    const layer = layerRef.current;
    const themeId = o.themeId;
    if (!layer || !o.road || !themeId || checkedSlot.current === themeId) return;
    checkedSlot.current = themeId;
    // Later changes come with each spin and beat.
    layer.setMods(o.road.story?.mods);
    layer.setMap(o.road.story?.mapPieces, o.road.story?.mapOf);
    void (async () => {
      const { profile, road } = opts.current;
      if (profile.storyMode === 'ask') {
        // The morning card and a waiting beat come after him too: nothing stands before the first spin.
        if (!spunHere.current) await new Promise<void>((go) => (welcomeGo.current = go));
        funnelBeacon('beat_seen', { playerId: opts.current.playerId, detail: 'welcome' });
        const yes = await (layerRef.current ?? layer).welcome();
        profile.setStoryPrefs({ storyMode: yes ? 'on' : 'off' });
      } else if (!profile.roadSlotsSeen.includes(themeId) && !road?.roadStep) {
        // A road already under way (restored before this was kept) isn't new.
        layer.react({ kind: 'newRoad', themeId }, paidSpins.current);
      }
      profile.markRoadSeen(themeId);
      // Unless the player opened the day's bonus from the map themselves.
      if (!storyBridge.dailyReleased) await morning();
      const waiting = opts.current.road?.story?.pending;
      if (waiting && !pendingBeat.current) await playBeat(waiting);
    })();
  }, [mounted, o.road, o.themeId, morning, playBeat]);

  // A run that just ended: Fen says how it went.
  useEffect(() => {
    if (o.run.phase !== 'idle') {
      lastRun.current = o.run;
      return;
    }
    const ended = lastRun.current;
    lastRun.current = null;
    if (ended) layerRef.current?.react({ kind: 'runCollected', paid: !potEmpty(ended.pot) }, paidSpins.current);
  }, [o.run]);

  /** A spin's response, as the reels land. */
  const onSpinLanded = useCallback((response: SpinSlotResponse) => {
    const layer = layerRef.current;
    if (!layer) return;
    spunHere.current = true;
    if (!response.wasFreeSpin) paidSpins.current++;
    if (opts.current.profile.storyMode === 'ask') layer.settleWelcome();
    const story = response.road?.story;
    // A new modifier's chip waits for its title card.
    const mods = { ...(story?.mods ?? {}) };
    if (response.beat?.kind === 'mod' && response.beat.mod) delete mods[response.beat.mod];
    layer.setMods(mods);
    // A map piece found on this spin joins the chip once it has flown there.
    const beat = response.beat;
    const pieces = beat?.kind === 'map' ? Math.max(0, (beat.pieces ?? 1) - 1) : story?.mapPieces;
    layer.setMap(pieces, story?.mapOf);
    if (response.beat) pendingBeat.current = response.beat;
    const spin = paidSpins.current;
    const won = response.settlement.totalWinnings;
    if (won > 0) quietSpins.current = 0;
    else if (!response.wasFreeSpin) quietSpins.current++;
    if (response.beat || response.boardRun) return;
    if (!saidFirstCollect.current && (response.road?.trail.collected ?? 0) > 0) {
      saidFirstCollect.current = true;
      layer.react({ kind: 'firstCollect' }, spin);
    } else if (won >= 10 * response.settlement.bet) {
      layer.react({ kind: 'bigWin' }, spin);
    } else if (!saidQuiet.current && quietSpins.current >= 5) {
      saidQuiet.current = true;
      layer.react({ kind: 'quiet' }, spin);
    }
  }, []);

  /** The reels are idle: play a beat that's waiting. */
  const onSlotIdle = useCallback(() => {
    // A first visit's first spin has settled: Fen's welcome now.
    const go = welcomeGo.current;
    if (go && spunHere.current) {
      welcomeGo.current = null;
      go();
      return;
    }
    const beat = pendingBeat.current;
    if (!beat || playing.current || opts.current.run.phase !== 'idle') return;
    pendingBeat.current = null;
    void playBeat(beat);
  }, [playBeat]);

  /** Leaving the slot: the first time this session, show the takings card.
   *  Resolves true to go on leaving, false to stay. */
  const beforeExit = useCallback(async (): Promise<boolean> => {
    const layer = layerRef.current;
    const { playerId, enabled, road } = opts.current;
    if (!enabled || !layer || !playerId || takingsShown.current || layer.busy) return true;
    if (paidSpins.current < TAKINGS_MIN_SPINS) return true;
    takingsShown.current = true;
    const takings = await fetchTakings(playerId, SESSION_STARTED);
    if (!takings) return true;
    funnelBeacon('takings_shown', { playerId, detail: String(takings.spins) });
    const choice = await layer.showTakings({ takings, lights: road ? { ready: road.ready, readyMax: road.readyMax } : null });
    return choice === 'leave';
  }, []);

  return {
    /** What the story shows now (browser tests, dev builds). */
    inspect: () => layerRef.current?.inspect() ?? null,
    /** True while the story has a beat or a card on screen. */
    busy: () => playing.current || (layerRef.current?.busy ?? false),
    onSpinLanded,
    onSlotIdle,
    beforeExit,
  };
}

/** The story's sounds, as `story:<name>`. */
function storyAudioEntries() {
  return Object.fromEntries(
    Object.entries(STORY_SOUNDS).map(([, alias]) => {
      const name = alias.slice('story:'.length);
      return [`story_${name}`, { alias, src: storyAudioUrl(name), path: `audio/story/${name}.mp3` }];
    }),
  );
}

const AUDIO = import.meta.glob<{ default: string }>('../assets/audio/story/*.mp3', { eager: true });

function storyAudioUrl(name: string): string {
  return AUDIO[`../assets/audio/story/${name}.mp3`]?.default ?? '';
}
